/**
 * Scalekit browser-session adapter (US-008).
 *
 * Authorization Code + PKCE (S256) against Scalekit through `oidc-client-ts`,
 * with the same behavioural guarantees `src/auth/keycloak.ts` provides and the
 * provider-neutral contract in `src/__tests__/authProviderContract.test.ts`
 * pins. keycloak-js stays wired for rollback; which adapter runs is decided by
 * `VITE_AUTH_PROVIDER` in `src/auth/authProvider.ts` and defaults to keycloak,
 * so this file ships INERT exactly like the backend's
 * `lutiq.identity.resolver.scalekit.dashboard.enabled=false`.
 *
 * <h2>Where the tokens live</h2>
 *
 * Access and ID tokens live in memory ONLY: `userStore` is an
 * `InMemoryWebStorage`, so nothing token-shaped survives a reload, a
 * `localStorage` dump, or an XSS read of web storage. The one browser-storage
 * dependency is the transient PKCE transaction (`code_verifier`, `state`,
 * `nonce`) — that is the minimum a redirect-based public client can work with,
 * because the verifier has to survive the full-page navigation to the
 * authorization server and back. It prefers `sessionStorage` and, ONLY when
 * sessionStorage is unusable, degrades to `localStorage` (see
 * {@link transientStateStorage} for why memory is not an acceptable
 * fallback there). Tokens never reach either store under any configuration.
 * No refresh token is ever requested: `offline_access` is stripped from the
 * configured scope (see {@link resolveScope}) and renewal is a `prompt=none`
 * iframe, not a refresh-token grant.
 *
 * <h2>What is attested and what is not</h2>
 *
 * Metadata is configured EXPLICITLY — no discovery fetch on the boot path —
 * and every endpoint is independently overridable by a `VITE_` env var. The
 * issuer-derived defaults carry the same evidence discipline as
 * `dashboard/backend/.../ScalekitEndpoints.java`:
 *
 *   - `{issuer}/oauth/token` — ATTESTED (the kc-sub backfill script POSTed here
 *     in production until its Phase 7 retirement in 2026-09;
 *     `quarkus.oidc.mcp-scalekit` uses the same host).
 *   - `{issuer}/keys` — ATTESTED (`quarkus.oidc.mcp-scalekit.jwks-path=keys`).
 *   - `{issuer}/oauth/authorize` — **NOT ATTESTED in this repository.**
 *   - `{issuer}/oidc/logout` — **NOT ATTESTED in this repository.**
 *   - the federated-login query param (Google) — **NOT ATTESTED**, and omitted
 *     entirely unless configured.
 *
 * The unattested defaults MUST be checked against the vendor's own
 * documentation before `VITE_AUTH_PROVIDER=scalekit` is set in ANY
 * environment. That is why each one has its own env override: a doc check that
 * disagrees is a config change, not a code change.
 */

import * as Sentry from '@sentry/react';
import {
  InMemoryWebStorage,
  UserManager,
  WebStorageStateStore,
  type OidcMetadata,
  type User,
  type UserManagerSettings,
} from 'oidc-client-ts';

import { isTransientAuthError } from './authErrors';
import {
  SCALEKIT_RECOVERY_LOOP_GUARD,
  SCALEKIT_RECOVERY_REDIRECT_PENDING,
  buildRedirectUri as buildNeutralRedirectUri,
  cleanCurrentUrl,
  createRecoveryBudget,
  ensureSecureContext as ensureNeutralSecureContext,
  hasAuthorizationResponse,
} from './browserSession';
import { getRuntimeScalekitConfig } from './runtimeAuthConfig';

// ───────────────────────────────────────────────────────────────────────────
// Configuration
// ───────────────────────────────────────────────────────────────────────────

const envValue = (raw: unknown): string | undefined => {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Dev default only. Production is `identity.lutiq.com` (PRD) — a Scalekit
 * CUSTOM DOMAIN. Staging is NOT `identity-stg.lutiq.com`, and never could have
 * been: Scalekit documents custom-domain CNAMEs as production-only, stg is a
 * Development environment, so the staging issuer is the NATIVE environment
 * host `https://lutiq.scalekit.dev` — the same host that serves the management
 * API, because Scalekit serves both from the environment host until a custom
 * domain replaces the OIDC one.
 *
 * Neither is hardcoded as policy: every deployment sets `VITE_SCALEKIT_ISSUER`
 * (or is served one by `GET /api/config/public`), and this value exists so a
 * developer running `npm run dev` against staging gets something coherent
 * rather than a crash. `scripts/scalekit/ensure-dashboard-auth.py`'s `issuer`
 * docstring and `infrastructure/environments/stg/terraform.tfvars` carry the
 * same correction; this was the last copy still naming a host nobody can
 * provision.
 */
const DEFAULT_ISSUER = 'https://lutiq.scalekit.dev';

/**
 * Dev default only, and deliberately not a real Scalekit client id (those are
 * `skc_…`-shaped). A deployment that forgets `VITE_SCALEKIT_CLIENT_ID` gets an
 * authorize request the provider rejects outright rather than one that
 * silently authenticates against the wrong client.
 */
const DEFAULT_CLIENT_ID = 'lutiq-dashboard-spa';

/** PKCE method. S256 only — `plain` binds nothing an attacker cannot compute. */
export const SCALEKIT_PKCE_CHALLENGE_METHOD = 'S256';

/**
 * The SPA-served route the `prompt=none` renewal iframe lands on. Same-site
 * with the identity domain (`*.lutiq.com`), so the SSO cookie flows into the
 * frame. `src/main.tsx` handles this path BEFORE React boots — booting the
 * whole dashboard inside a renewal iframe would be a second, invisible app.
 */
export const SCALEKIT_SILENT_CALLBACK_PATH = '/auth/silent-callback';

/**
 * The ONE route an interactive authorization response is delivered to.
 *
 * <h2>Why this is fixed rather than "wherever the user was"</h2>
 *
 * This file used to send the user's own current location as the `redirect_uri`,
 * justified by a `https://dashboard.lutiq.com/*` wildcard allowlist entry.
 * <b>That entry does not exist and cannot.</b> Verified read-only against the
 * live stg authorization endpoint on 2026-08-17 (client
 * `spac_139123375254536971`), which matches registered callback URLs by EXACT
 * STRING:
 *
 * <pre>
 *   GET {issuer}/oauth/authorize?...&redirect_uri=https://dashboard-stg.lutiq.com/auth/callback
 *     → 302 Location: /auth/login?auth_request_id=…
 *   GET {issuer}/oauth/authorize?...&redirect_uri=https://dashboard-stg.lutiq.com/brands/123
 *     → 400 "The requested redirect_uri is missing in the client configuration."
 * </pre>
 *
 * The same 400 comes back for a registered entry with a query string appended
 * (`/auth/callback?next=…`) and for one with a trailing slash added
 * (`/auth/callback/`), so neither a path wildcard nor a query-parameter carrier
 * is available. And Scalekit caps a client at FIVE Allowed callback URLs
 * (`scripts/scalekit/README.md` quirk 24), so "just register every route" is
 * not an option either — the five slots are already allocated, deliberately, by
 * `scripts/scalekit/ensure-dashboard-auth.py`.
 *
 * <h2>How the deep link survives anyway</h2>
 *
 * In the OIDC `state`, which is what `state` is for. `oidc-client-ts` stores
 * arbitrary caller state alongside the PKCE transaction and hands it back as
 * `User.state` from `signinCallback()` — see {@link signinStateFor} and
 * {@link restorePostLoginLocation}. It never travels on the wire, so unlike a
 * `redirect_uri` carrier it also keeps the user's path (and anything in its
 * query string, e.g. `/accept-invite?token=…`) out of the provider's logs.
 *
 * <h2>Registration</h2>
 *
 * `{origin}/auth/callback` is ALREADY registered for both origins the SPA is
 * served from — slots 1 and 4 of the five. This fix therefore needs no change
 * to the live client's configuration; it needs the SPA to stop asking for URLs
 * that were never in it.
 *
 * <p>Served by the `dashboard-static` Worker's index.html fallback like every
 * other extensionless path, and — unlike {@link SCALEKIT_SILENT_CALLBACK_PATH}
 * — deliberately NOT special-cased in `src/main.tsx`: this one is a full app
 * boot, because the session it completes is the user's own.
 */
export const SCALEKIT_CALLBACK_PATH = '/auth/callback';

/** Routes that exist to service the OAuth flow; never a place to land a user. */
const FLOW_ONLY_PATHS = new Set([SCALEKIT_CALLBACK_PATH, SCALEKIT_SILENT_CALLBACK_PATH]);

/** sessionStorage prefix for the transient PKCE transaction. Never tokens. */
const TRANSIENT_STATE_PREFIX = 'lutiq:scalekit:oidc.';

const RECOVERY_STORAGE_KEY = 'lutiq:scalekit:recovery-attempts';

/**
 * Wall-clock marker for the most recent full-page renewal fallback. In
 * sessionStorage because the thing it has to outlive is the navigation the
 * fallback itself performs.
 */
const FULL_PAGE_RENEWAL_AT_KEY = 'lutiq:scalekit:last-full-page-renewal';

/** Matches keycloak's ladder: two self-heal redirects, then a terminal state. */
const MAX_RECOVERY_ATTEMPTS = 2;

/**
 * Seconds of remaining validity below which the access token is renewed. The
 * point of a non-zero floor is that renewing only once the token has ALREADY
 * expired guarantees a window of 401s on every in-flight request. Mirrors
 * `keycloak.updateToken(30)`.
 */
export const SCALEKIT_TOKEN_MIN_VALIDITY_SECONDS = 30;

/**
 * Two full-page renewals closer together than this are not renewals; they are a
 * loop.
 *
 * The redirect only pays for itself if the session it brings back stays usable
 * for longer than the min-validity floor. If it does not — a token lifetime at
 * or below that floor, with a blocked iframe — every reload bootstraps and
 * immediately needs renewing again, and neither of the other two bounds can
 * see it: `fullPageFallbackSpent` dies with the page, and a SUCCESSFUL
 * bootstrap clears the sessionStorage budget on the way through. Twice the
 * floor is the smallest interval that leaves an honest renewal — one per token
 * lifetime — completely untouched.
 */
const MIN_SECONDS_BETWEEN_FULL_PAGE_RENEWALS = SCALEKIT_TOKEN_MIN_VALIDITY_SECONDS * 2;

/**
 * A browser public client must never hold a refresh token: it cannot keep one
 * secret, and a stolen one is a long-lived credential. Renewal goes through
 * the `prompt=none` iframe instead, which is bound to the SSO cookie.
 */
const FORBIDDEN_SCOPES = new Set(['offline_access']);

/**
 * The runtime-then-build-time ladder, applied field by field (US-011).
 *
 * Every resolver below reads `GET /api/config/public` first, then its
 * `VITE_SCALEKIT_*` env var, then a local default. Field by field rather than
 * all-or-nothing on purpose: the backend serves only what it was configured
 * with and OMITS the rest, so an unset endpoint override falls straight through
 * to the derivation that carries this file's ATTESTED / NOT-ATTESTED evidence —
 * which stays the single statement of what is verified.
 */
const runtimeValue = (
  pick: (config: NonNullable<ReturnType<typeof getRuntimeScalekitConfig>>) => string | undefined,
): string | undefined => {
  const config = getRuntimeScalekitConfig();
  return config ? envValue(pick(config)) : undefined;
};

const resolveIssuer = (): string =>
  (
    runtimeValue((config) => config.issuer) ??
    envValue(import.meta.env.VITE_SCALEKIT_ISSUER) ??
    DEFAULT_ISSUER
  ).replace(/\/+$/, '');

const resolveClientId = (): string =>
  runtimeValue((config) => config.clientId) ??
  envValue(import.meta.env.VITE_SCALEKIT_CLIENT_ID) ??
  DEFAULT_CLIENT_ID;

/**
 * The organization whose tokens the backend accepts. Unlike issuer and client
 * id there is no safe local default: omitting the selector is preferable to
 * inventing a tenant, and deployed runtime config treats absence as a reason
 * to keep the browser on Keycloak.
 */
const resolveOrganizationId = (): string | undefined => {
  const configured =
    runtimeValue((config) => config.organizationId) ??
    envValue(import.meta.env.VITE_SCALEKIT_ORGANIZATION_ID);
  return configured?.startsWith('org_') && configured.length > 4 ? configured : undefined;
};

/** Scalekit's documented tenant-routing query parameter. */
const resolveOrganizationParams = (): Record<string, string> | undefined => {
  const organizationId = resolveOrganizationId();
  return organizationId ? { organization_id: organizationId } : undefined;
};

const resolveScope = (): string => {
  const configured =
    runtimeValue((config) => config.scope) ??
    envValue(import.meta.env.VITE_SCALEKIT_SCOPE) ??
    'openid profile email';
  const scopes = configured.split(/\s+/).filter((scope) => scope && !FORBIDDEN_SCOPES.has(scope));
  if (!scopes.includes('openid')) scopes.unshift('openid');
  return scopes.join(' ');
};

/**
 * Explicit metadata. Discovery is deliberately OFF — the same decision the
 * backend made for `quarkus.oidc.mcp-scalekit.discovery-enabled=false` — so a
 * cold boot never depends on a third-party document being reachable, and so
 * the endpoints this client dials are reviewable in the repository.
 */
const resolveMetadata = (issuer: string): Partial<OidcMetadata> => ({
  issuer,
  // STILL NOT ATTESTED. The 2026-08-16 verification against Scalekit's official
  // OpenAPI document (see dashboard/backend/.../ScalekitEndpoints.java) covered
  // the MANAGEMENT API — user create, delete, session revocation, and the fact
  // that /oauth/token is client_credentials against
  // https://{subdomain}.scalekit.dev|.com or a custom domain. It says NOTHING
  // about the browser authorize and logout routes, so this marker stays exactly
  // as it was. Upgrading it on the strength of a nearby attestation is how a
  // guess becomes a fact nobody rechecks.
  authorization_endpoint:
    runtimeValue((config) => config.authorizeEndpoint) ??
    envValue(import.meta.env.VITE_SCALEKIT_AUTHORIZE_ENDPOINT) ??
    `${issuer}/oauth/authorize`,
  // ATTESTED twice: the kc-sub backfill script POSTed to /oauth/token until it
  // was retired in Phase 7 (2026-09), and the same path and grant are in the
  // vendor's spec (2026-08-16).
  token_endpoint:
    runtimeValue((config) => config.tokenEndpoint) ??
    envValue(import.meta.env.VITE_SCALEKIT_TOKEN_ENDPOINT) ??
    `${issuer}/oauth/token`,
  // ATTESTED: quarkus.oidc.mcp-scalekit.jwks-path=keys.
  jwks_uri:
    runtimeValue((config) => config.jwksEndpoint) ??
    envValue(import.meta.env.VITE_SCALEKIT_JWKS_ENDPOINT) ??
    `${issuer}/keys`,
  // STILL NOT ATTESTED — same reasoning as authorization_endpoint above.
  end_session_endpoint:
    runtimeValue((config) => config.endSessionEndpoint) ??
    envValue(import.meta.env.VITE_SCALEKIT_END_SESSION_ENDPOINT) ??
    `${issuer}/oidc/logout`,
});

/**
 * Extra authorize params for the federated ("Continue with Google") button.
 * The Keycloak path sends `idpHint`; Scalekit's equivalent is an authorize
 * query param whose NAME and VALUE are **not attested here**, so both are env
 * driven and the whole param is omitted when unset — exactly how
 * `VITE_KEYCLOAK_GOOGLE_IDP_HINT` behaves. An omitted hint degrades to the
 * provider's own account chooser; a guessed one would produce an authorize
 * request the provider rejects.
 */
const FEDERATED_PARAMS_THAT_OVERRIDE_ORGANIZATION = new Set([
  'provider',
  'connection_id',
  'organization_id',
]);

const resolveFederatedParams = (organizationPinned = false): Record<string, string> => {
  const value =
    runtimeValue((config) => config.googleHint) ??
    envValue(import.meta.env.VITE_SCALEKIT_GOOGLE_HINT);
  if (!value) return {};
  const name =
    runtimeValue((config) => config.googleParam) ??
    envValue(import.meta.env.VITE_SCALEKIT_GOOGLE_PARAM) ??
    'provider';
  // Scalekit resolves provider first (and ignores every other selector), then
  // connection_id, then organization_id. Sending either higher-precedence
  // hint would route before the exact organization the backend accepts as oid;
  // an organization_id hint could overwrite that exact value during merging.
  // Keep the organization-scoped chooser instead when the deployment is pinned.
  if (organizationPinned && FEDERATED_PARAMS_THAT_OVERRIDE_ORGANIZATION.has(name)) return {};
  return { [name]: value };
};

/** True when `store` accepts a write (privacy modes expose-but-throw). */
const storageWritable = (store: Storage | undefined): store is Storage => {
  if (!store) return false;
  try {
    const probe = `${TRANSIENT_STATE_PREFIX}probe`;
    store.setItem(probe, '1');
    store.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

/**
 * The transient PKCE transaction store: `sessionStorage`, then `localStorage`,
 * then memory. NEVER tokens — only the `state`/`nonce`/`code_verifier`
 * transaction, which is worthless without the authorization code it binds.
 *
 * Why the ladder is ordered this way: the transaction MUST survive the
 * full-page navigation to the authorization server and back — an in-memory
 * store is wiped by that navigation, so under it `signinCallback()` can never
 * find the verifier `signinRedirect()` wrote and every interactive login dies
 * on a state mismatch. sessionStorage is the minimum (per-tab, gone on close);
 * where a privacy mode breaks it, localStorage is the last store that still
 * survives the hop — the same degradation keycloak-js's CallbackStorage ships
 * in this app today, so this is parity, not a loosening. Memory remains only
 * as the do-not-crash floor for environments with no writable web storage at
 * all, where interactive login is structurally impossible anyway and the
 * bounded recovery ladder is what keeps the failure finite.
 */
const transientStateStorage = (): Storage => {
  if (typeof window !== 'undefined') {
    try {
      if (storageWritable(window.sessionStorage)) return window.sessionStorage;
    } catch {
      // accessing the getter itself can throw — fall through
    }
    try {
      if (storageWritable(window.localStorage)) return window.localStorage;
    } catch {
      // fall through to memory
    }
  }
  return new InMemoryWebStorage();
};

// ───────────────────────────────────────────────────────────────────────────
// Redirect target and deep-link carrier
// ───────────────────────────────────────────────────────────────────────────

/** The registered callback URI for the origin the SPA is being served from. */
const callbackRedirectUri = (): string => buildNeutralRedirectUri(SCALEKIT_CALLBACK_PATH);

/**
 * Where to put the user once the callback completes, carried in the OIDC
 * `state`.
 *
 * <p>`oidc-client-ts` keeps this beside the PKCE transaction in the same
 * transient store (see {@link transientStateStorage}) and returns it as
 * `User.state`. It is therefore exactly as durable as the `code_verifier` — if
 * one survives the round trip, so does the other — and exactly as private: it
 * is never a query parameter on the authorize URL, so an invite token sitting
 * in the captured location cannot reach the provider's logs.
 */
type ScalekitSigninState = { returnTo: string };

/**
 * Capture the post-login target for an optional explicit path.
 *
 * With no argument this is the user's current location, OAuth-param-clean —
 * the same {@link buildNeutralRedirectUri} call the `redirect_uri` used to be,
 * so the deep-link semantics US-008 pinned are preserved verbatim, including
 * the surgical stripping that keeps `/accept-invite?token=…` reachable.
 *
 * A capture that points at a flow-only route is rewritten to the entry point:
 * returning a user to `/auth/callback` after login would land them on a route
 * whose only job is to be navigated away from.
 */
const signinStateFor = (path?: string): ScalekitSigninState => {
  const returnTo = buildNeutralRedirectUri(path);
  try {
    if (FLOW_ONLY_PATHS.has(new URL(returnTo).pathname)) {
      return { returnTo: buildNeutralRedirectUri('/') };
    }
  } catch {
    // Unparseable capture — fall through and let the reader validate it.
  }
  return { returnTo };
};

/**
 * The same-origin target inside a returned `User.state`, or `undefined`.
 *
 * <p>The same-origin check is not ceremony. The carrier round-trips through
 * browser storage, and the SPA acts on the result with `history.replaceState`;
 * accepting a foreign absolute URL from it would make the login path an open
 * redirect for anything that can write one key of sessionStorage.
 */
const returnToFromState = (state: unknown): string | undefined => {
  const raw = (state as { returnTo?: unknown } | null | undefined)?.returnTo;
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return undefined;
  }
  if (url.origin !== window.location.origin) return undefined;
  if (FLOW_ONLY_PATHS.has(url.pathname)) return undefined;
  return `${url.origin}${url.pathname}${url.search}${url.hash}`;
};

/**
 * Put the browser on the page the user asked for before they were sent to log
 * in, and get the consumed `?code=…&state=…` out of the URL on the way.
 *
 * <p>Runs BEFORE anything else can observe the location — the same precondition
 * {@link cleanCurrentUrl} carried, which `AuthContext` relies on to start
 * Sentry Session Replay without the callback params in the replay envelope.
 * `history.replaceState` (not `assign`) because this must not be a navigation:
 * the SPA has a live session in memory and reloading would throw it away.
 *
 * <p>`App.tsx` renders its `BrowserRouter` only once `loading` is false, i.e.
 * strictly after this has run, so the router reads the restored location rather
 * than the callback route.
 *
 * <p>No carrier means a callback this adapter did not start (or one issued
 * before the carrier existed). Then the only honest thing is the old behaviour
 * — strip the params in place — except that standing on the callback route is
 * not a place to leave anyone, so that one case falls back to the entry point.
 */
const restorePostLoginLocation = (state: unknown): void => {
  const target = returnToFromState(state);
  if (target) {
    if (target !== window.location.href) window.history.replaceState(null, '', target);
    return;
  }
  if (FLOW_ONLY_PATHS.has(window.location.pathname)) {
    window.history.replaceState(null, '', buildNeutralRedirectUri('/'));
    return;
  }
  cleanCurrentUrl();
};

const buildSettings = (): UserManagerSettings => {
  const issuer = resolveIssuer();
  const organizationParams = resolveOrganizationParams();
  return {
    authority: issuer,
    client_id: resolveClientId(),
    // ONE registered callback route, never the user's own location. Scalekit
    // matches redirect_uris by exact string and holds at most five of them —
    // see SCALEKIT_CALLBACK_PATH for the live probe that settles it. The deep
    // link rides in `state` instead.
    redirect_uri: callbackRedirectUri(),
    post_logout_redirect_uri: buildNeutralRedirectUri('/'),
    silent_redirect_uri: buildNeutralRedirectUri(SCALEKIT_SILENT_CALLBACK_PATH),
    response_type: 'code',
    response_mode: 'query',
    scope: resolveScope(),
    metadata: resolveMetadata(issuer),
    ...(organizationParams ? { extraQueryParams: organizationParams } : {}),
    // PKCE is the whole point of this client. oidc-client-ts uses S256
    // whenever PKCE is enabled; stating the negative explicitly means a
    // future "just disable it to debug" edit is visible in review.
    disablePKCE: false,
    loadUserInfo: false,
    // Renewal is driven on demand through a single-flight promise
    // (see `renewAccessToken`). oidc-client-ts's own timer would issue a
    // SECOND, uncoordinated renewal and defeat the coalescing guarantee.
    automaticSilentRenew: false,
    // No check-session iframe: parity with keycloak's `checkLoginIframe:false`.
    monitorSession: false,
    // Nothing to revoke — this client never holds a refresh token.
    revokeTokensOnSignout: false,
    includeIdTokenInSilentRenew: false,
    validateSubOnSilentRenew: true,
    // TOKENS IN MEMORY ONLY. Do not "helpfully" swap this for
    // window.sessionStorage: it would put a bearer token in web storage.
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    // Transient PKCE/state/nonce only.
    stateStore: new WebStorageStateStore({
      store: transientStateStorage(),
      prefix: TRANSIENT_STATE_PREFIX,
    }),
  };
};

// ───────────────────────────────────────────────────────────────────────────
// Module state
// ───────────────────────────────────────────────────────────────────────────

type BootstrapPhase = 'idle' | 'pending' | 'success' | 'failed';

let manager: UserManager | null = null;
let bootstrapState: { phase: BootstrapPhase; promise: Promise<boolean> | null } = {
  phase: 'idle',
  promise: null,
};

/**
 * The ONE in-flight renewal. Every concurrent `getAccessToken()` caller awaits
 * this same promise, so N parallel API requests produce ONE authorization
 * round trip rather than N racing ones that invalidate each other's state.
 */
let inFlightRenewal: Promise<string | null> | null = null;

/**
 * Bumped whenever the session is torn down (logout, recovery). A renewal that
 * was already in flight across a teardown resolves to `null` instead of
 * publishing a token that belongs to a session the user has left — which is
 * the concrete shape "a rotated token must not be replayable" takes here.
 */
let sessionGeneration = 0;

/** Exactly one full-page renewal fallback per page load. */
let fullPageFallbackSpent = false;

/** Set once recovery gives up. Bounded terminal state, never a redirect loop. */
let recoveryTerminal = false;

/** Subject (`sub`) of the current session. A UUID — never a credential. */
let currentSubject: string | undefined;

/** Min-validity the adapter demanded on its most recent freshness decision. */
let lastRenewalMinValiditySeconds: number | undefined;

const recoveryBudget = createRecoveryBudget(
  RECOVERY_STORAGE_KEY,
  MAX_RECOVERY_ATTEMPTS,
  SCALEKIT_RECOVERY_LOOP_GUARD,
);

/**
 * When the last full-page renewal fallback started, or `undefined` if there was
 * none this tab (or sessionStorage is unavailable, in which case the per-page
 * flag and the recovery budget remain the bounds).
 */
const lastFullPageRenewalAt = (): number | undefined => {
  try {
    const raw = Number(sessionStorage.getItem(FULL_PAGE_RENEWAL_AT_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : undefined;
  } catch {
    return undefined;
  }
};

const recordFullPageRenewal = (at: number): void => {
  try {
    sessionStorage.setItem(FULL_PAGE_RENEWAL_AT_KEY, String(at));
  } catch {
    // ignore — the per-page-load flag and the budget still bound the ladder.
  }
};

const ensureSecureContext = (): void =>
  ensureNeutralSecureContext(SCALEKIT_RECOVERY_REDIRECT_PENDING);

/**
 * What `manager` was built from, as a comparable string.
 *
 * <p>`buildSettings()` snapshots the issuer, client id, scope and metadata, and
 * the `UserManager` holds them for its lifetime. Before US-011 that was fine —
 * every input was a build-time constant. Now the answer can arrive from
 * `GET /api/config/public` mid-page-load, so a manager built before the adopt
 * would keep dialling the boot-time defaults for the rest of the page's life,
 * silently, while every other reader saw the new config.
 *
 * <p><b>The signature compares the SETTINGS, not the config generation</b>, and
 * that distinction is load-bearing: rebuilding drops the in-memory user store,
 * i.e. it ends the session. An adopt that resolves to the same client — the
 * overwhelmingly common case, since the served config normally agrees with the
 * build-time flag — must therefore be a no-op rather than a silent logout.
 *
 * <p><b>THIS IS NOT DEFENCE IN DEPTH BEHIND AN ORDERING GUARANTEE — IT IS THE
 * ONLY THING HOLDING THE PRODUCTION CASE.</b> This paragraph used to claim
 * `AuthContext` awaits `loadRuntimeAuthConfig()` BEFORE `startSession()`, so
 * that the manager was built once from the final config and the check was a
 * belt for a rare order. That ordering was removed in 5f4946622: an
 * unconditional await put a `GET /api/config/public` round trip in front of
 * authentication on every page load, and `requestJson` sets no timeout, so a
 * HANGING config endpoint hung the boot spinner forever. The fetch is now
 * awaited only when this page load is completing a callback the Scalekit
 * adapter started. On EVERY OTHER PAGE LOAD — the common one — the config
 * lands after the manager has already been built from the build-time defaults,
 * which is precisely the case this signature catches. `publicConfig.ts` was
 * corrected on the same argument in 5596be4cb; this copy was missed.
 *
 * <p>So: do not delete this check as dead defence, and do not "restore" the
 * ordering it used to lean on.
 */
let managerSignature: string | null = null;

/**
 * The identity-bearing half of {@link buildSettings}, without building it.
 *
 * <p>Deliberately not `JSON.stringify(buildSettings())`: that would run
 * {@link transientStateStorage} — a real `setItem`/`removeItem` probe — on
 * every `getAccessToken()`, i.e. on every API request the SPA makes.
 */
const clientSignature = (): string => {
  const issuer = resolveIssuer();
  return JSON.stringify([
    issuer,
    resolveClientId(),
    resolveOrganizationId(),
    resolveScope(),
    resolveMetadata(issuer),
  ]);
};

const getManager = (): UserManager => {
  const signature = clientSignature();
  if (!manager || managerSignature !== signature) {
    manager = new UserManager(buildSettings());
    managerSignature = signature;
  }
  return manager;
};

/** Test-only: reset the module singleton. Production code must never call it. */
export const __resetForTests = (): void => {
  manager = null;
  managerSignature = null;
  bootstrapState = { phase: 'idle', promise: null };
  inFlightRenewal = null;
  sessionGeneration += 1;
  fullPageFallbackSpent = false;
  recoveryTerminal = false;
  currentSubject = undefined;
  lastRenewalMinValiditySeconds = undefined;
  recoveryBudget.clear();
  try {
    sessionStorage.removeItem(FULL_PAGE_RENEWAL_AT_KEY);
  } catch {
    // ignore
  }
};

/** Test-only: the min-validity the adapter last demanded, in seconds. */
export const __lastRenewalMinValiditySeconds = (): number | undefined =>
  lastRenewalMinValiditySeconds;

/**
 * Whether THIS adapter has a PKCE transaction outstanding in browser storage.
 *
 * <h2>What it is for</h2>
 *
 * It answers one question, synchronously and with no network: <b>did the
 * Scalekit adapter start the authorization flow whose response is in the URL
 * right now?</b> `AuthContext` uses it to decide whether the boot path must
 * wait for `GET /api/config/public` before dispatching. A callback has to be
 * completed by the adapter that started it — but only THIS adapter's callbacks
 * need the runtime config resolved first, and only this adapter leaves a
 * transaction behind.
 *
 * <p>On a Keycloak deployment there is never one, so the boot path never waits
 * on the network. That is the whole point: the runtime lookup must cost a
 * production page load nothing.
 *
 * <h2>Why a key scan and not a state comparison</h2>
 *
 * `oidc-client-ts` writes the transaction under `${prefix}${state}`, so the
 * exact key is knowable from the URL's `state`. Matching it would be more
 * precise and WORSE: a stale transaction from an abandoned flow would then read
 * as "not ours", and the abandoned-flow case is exactly when the two adapters
 * are most likely to be mid-cutover. Any pending transaction means this adapter
 * has been driving recently, which is the honest threshold for "wait and find
 * out".
 *
 * <p>Storage that cannot be read at all answers FALSE — the same degradation
 * the flow itself would take, because a callback whose verifier is gone cannot
 * be completed by either adapter.
 */
export const hasPendingTransaction = (): boolean => {
  try {
    const store = transientStateStorage();
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key && key.startsWith(TRANSIENT_STATE_PREFIX)) return true;
    }
  } catch {
    // Privacy modes expose-but-throw. Unreadable is "no evidence", not "yes".
  }
  return false;
};

/**
 * Drop every pending transaction this adapter left behind.
 *
 * <p>For ONE caller: `AuthContext.boot()`, when an authorization response is in
 * the URL, this adapter started it, and the runtime config says the deployment
 * has ROLLED BACK to keycloak. The code in that URL is dead either way — the
 * backend no longer accepts tokens from the issuer that minted it — but leaving
 * the transaction in storage would make the boot guard fire again on the next
 * load, and the one after, with no way out. Clearing it is what turns a
 * permanent stall into one wasted round trip.
 *
 * <p>NOT a logout and NOT part of the adapter's own flow: it removes only this
 * adapter's transient PKCE state, never a session.
 */
export const discardPendingTransaction = (): void => {
  try {
    const store = transientStateStorage();
    const doomed: string[] = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key && key.startsWith(TRANSIENT_STATE_PREFIX)) doomed.push(key);
    }
    doomed.forEach((key) => store.removeItem(key));
  } catch {
    // Same tolerance as the read above: storage we cannot touch holds nothing
    // we can act on.
  }
};

// ───────────────────────────────────────────────────────────────────────────
// Client description
// ───────────────────────────────────────────────────────────────────────────

export const describeClient = () => ({
  clientId: resolveClientId(),
  pkceChallengeMethod: SCALEKIT_PKCE_CHALLENGE_METHOD,
  /**
   * Scalekit's `iss` IS the environment base URL — there is no realm path
   * segment the way Keycloak has one. Normalising the trailing slash is the
   * whole transformation, and it matters: the backend pins
   * `quarkus.oidc.dashboard-scalekit.token.issuer` to an exact string, so a
   * client that authenticates against `…/` mints tokens every downstream
   * check rejects.
   */
  issuerFor: (idpBaseUrl: string): string => idpBaseUrl.replace(/\/+$/, ''),
});

/**
 * A URL on the SPA's own origin for an optional path, OAuth-param-clean.
 *
 * <p>NOT this adapter's `redirect_uri` — that is fixed at
 * {@link SCALEKIT_CALLBACK_PATH} because Scalekit matches registered callbacks
 * by exact string. This is the provider-neutral LOCATION builder the seam in
 * `src/auth/authProvider.ts` exposes: what it produces is where the user should
 * end up, which this adapter carries in `state` and the keycloak adapter (whose
 * realm really does allowlist `${DASHBOARD_BASE_URL}/*`) can still put on the
 * wire.
 */
export const buildRedirectUri = (path?: string): string => buildNeutralRedirectUri(path);

export const isSessionBootstrapped = (): boolean => bootstrapState.phase === 'success';

export const getSubject = (): string | undefined => currentSubject;

/** True once the recovery ladder has given up. The SPA renders, it never loops. */
export const isRecoveryExhausted = (): boolean => recoveryTerminal;

// ───────────────────────────────────────────────────────────────────────────
// Error classification
// ───────────────────────────────────────────────────────────────────────────

const errorText = (err: unknown): string => {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return typeof err === 'string' ? err : '';
};

/**
 * The machine-readable OAuth error code, read from the STRUCTURED field rather
 * than from the message.
 *
 * `oidc-client-ts` builds `ErrorResponse.message` as
 * `error_description || error`, so a provider that sends a human-readable
 * description hides the code from any message-substring test. Classifying by
 * message alone therefore works only against providers that send no
 * description — and a missed `login_required` on a cold boot is a redirect for
 * an anonymous visitor, i.e. the login loop this adapter exists to prevent.
 *
 * Present ONLY on an error the authorization server actually answered with,
 * which is what makes it usable as evidence in both classifiers below.
 */
const oauthErrorCode = (err: unknown): string => {
  const code = (err as { error?: unknown } | null | undefined)?.error;
  return typeof code === 'string' ? code.trim().toLowerCase() : '';
};

/**
 * The authorization server's way of saying "there is no session here, and I
 * will not create one without user interaction" (OIDC Core §3.1.2.6). On a
 * cold boot that is an anonymous visitor, not a failure — auto-redirecting
 * would make every public route unreachable and turn a provider that cannot
 * authenticate into an unbreakable cycle.
 */
const SESSION_ABSENT_ERRORS = [
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
  // Keycloak's wire-level vocabulary for "refresh against a dropped session".
  // Not OIDC-standard, but the rollback issuer speaks it and nothing else
  // legitimately produces the string, so classifying it costs nothing and a
  // provider that emits it is definitively reporting a dead session.
  'session_not_active',
];

const isSessionAbsent = (err: unknown): boolean => {
  // The code, when the provider gave one, IS the answer — do not second-guess
  // it with the prose beside it.
  const code = oauthErrorCode(err);
  if (code) return SESSION_ABSENT_ERRORS.includes(code);
  const text = errorText(err).toLowerCase();
  return SESSION_ABSENT_ERRORS.some((entry) => text.includes(entry));
};

/**
 * The iframe itself could not complete: a timeout, a blocked frame, or a
 * missing silent-callback route. Distinct from "the session is gone" because
 * only THIS class of failure justifies the one full-page redirect fallback —
 * a dead session must degrade to "no token" so the SPA renders a login
 * affordance instead of bouncing the user.
 */
const isIframeRenewalUnavailable = (err: unknown): boolean => {
  // A structured OAuth error code means the frame DID complete its round trip
  // and the authorization server answered. Whatever it answered, the iframe is
  // not the thing that is broken, so it can never justify a full-page
  // navigation — not even when the provider's prose happens to say "timed out".
  if (oauthErrorCode(err)) return false;
  if (isSessionAbsent(err)) return false;
  const text = errorText(err).toLowerCase();
  return (
    text.includes('errortimeout')
    || text.includes('timed out')
    || text.includes('timeout')
    || text.includes('iframe')
    || text.includes('frame window')
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Callback validation
// ───────────────────────────────────────────────────────────────────────────

const audiencesOf = (aud: unknown): string[] => {
  if (Array.isArray(aud)) return aud.filter((entry): entry is string => typeof entry === 'string');
  return typeof aud === 'string' ? [aud] : [];
};

/**
 * Fail-closed validation of whatever came back from a callback.
 *
 * `oidc-client-ts` already rejects a `state` or `nonce` that does not match the
 * transaction it stored, and refuses a response whose `code` it has no
 * verifier for. This is the layer ON TOP of that: the identity must also be
 * one this client asked for. An id_token minted by a DIFFERENT Scalekit
 * environment, or for a different client, is structurally valid and completely
 * unacceptable — the backend pins both `iss` and `aud`, so accepting one here
 * produces a session whose every API call 401s, with no clue why.
 *
 * On rejection the user is removed from the in-memory store BEFORE throwing,
 * so no code path can hand out the token that failed validation.
 */
const assertTrustedIdentity = async (user: User, um: UserManager): Promise<void> => {
  const profile = (user.profile ?? {}) as Record<string, unknown>;
  const expectedIssuer = String(um.settings.authority ?? '').replace(/\/+$/, '');
  const expectedAudience = String(um.settings.client_id ?? '');
  const expectedOrganization = String(
    um.settings.extraQueryParams?.organization_id ?? '',
  );
  const failures: string[] = [];

  if (String(profile.iss ?? '').replace(/\/+$/, '') !== expectedIssuer) failures.push('iss');
  if (!audiencesOf(profile.aud).includes(expectedAudience)) failures.push('aud');
  if (expectedOrganization && profile.oid !== expectedOrganization) failures.push('oid');
  if (!profile.sub) failures.push('sub');
  if (!user.access_token) failures.push('access_token');

  if (failures.length > 0) {
    try {
      await um.removeUser();
    } catch {
      // ignore — the throw below is what fails the flow closed.
    }
    currentSubject = undefined;
    // The failing claim names are safe to report; the token is not, and is
    // deliberately absent from the message.
    throw new Error(`scalekit-callback-identity-rejected:${failures.join(',')}`);
  }

  currentSubject = String(profile.sub);
};

// ───────────────────────────────────────────────────────────────────────────
// Recovery ladder
// ───────────────────────────────────────────────────────────────────────────

const markTerminal = (reason: string): void => {
  recoveryTerminal = true;
  Sentry.addBreadcrumb({
    category: 'auth',
    level: 'warning',
    message: SCALEKIT_RECOVERY_LOOP_GUARD,
    data: { reason },
  });
};

/**
 * Restart a broken flow. Always settles by REJECTING: with
 * `scalekit-recovery-redirect-pending` while a navigation is under way (the
 * SPA holds its spinner), or `scalekit-recovery-loop-guard-tripped` once the
 * budget is spent (the SPA gives up and renders). Collapsing the two would
 * either flash an unauthenticated screen mid-navigation or spin forever on a
 * dead provider.
 */
const attemptRecovery = async (): Promise<never> => {
  try {
    recoveryBudget.consume();
  } catch (err) {
    markTerminal('recovery-budget-exhausted');
    throw err;
  }

  cleanCurrentUrl();
  bootstrapState = { phase: 'idle', promise: null };
  inFlightRenewal = null;
  sessionGeneration += 1;
  currentSubject = undefined;

  // No `prompt: 'login'` — the user's Scalekit SSO cookie may still be valid;
  // a PKCE/state mismatch is a client-side problem and doesn't justify forcing
  // a credential re-entry.
  //
  // Returns to `/` and not to the deep link, unchanged from before: the flow
  // being recovered from is a BROKEN CALLBACK, so the location it would capture
  // is a callback URL rather than a page the user chose.
  const organizationParams = resolveOrganizationParams();
  await getManager().signinRedirect({
    redirect_uri: callbackRedirectUri(),
    state: signinStateFor('/'),
    ...(organizationParams ? { extraQueryParams: organizationParams } : {}),
  });
  throw new Error(SCALEKIT_RECOVERY_REDIRECT_PENDING);
};

export const recoverSession = async (): Promise<never> => {
  bootstrapState = { phase: 'idle', promise: null };
  return attemptRecovery();
};

/**
 * The ONE full-page renewal fallback, used only when the `prompt=none` iframe
 * cannot run at all. It preserves the user's deep link (unlike
 * {@link attemptRecovery}, which returns to `/` because the flow it recovers
 * from is a broken callback rather than a live page).
 *
 * Bounded three times over: once per page load by `fullPageFallbackSpent`,
 * across reloads by the shared sessionStorage budget, and — because a
 * successful bootstrap deliberately restores both of those — by a minimum
 * interval between consecutive fallbacks. Any of them spent means a terminal
 * state, never another redirect.
 */
const beginFullPageRenewalFallback = async (): Promise<void> => {
  if (fullPageFallbackSpent) {
    markTerminal('iframe-renewal-fallback-already-spent');
    return;
  }

  const now = Date.now();
  const previous = lastFullPageRenewalAt();
  if (previous !== undefined && now - previous < MIN_SECONDS_BETWEEN_FULL_PAGE_RENEWALS * 1000) {
    // The last redirect bought a session that was stale on arrival (or the
    // clock moved backwards). Repeating it is a loop, not a recovery. Spend the
    // per-page flag too, so a page that keeps polling the API reports this once
    // rather than filling the breadcrumb buffer with it.
    fullPageFallbackSpent = true;
    markTerminal('iframe-renewal-fallback-too-soon');
    return;
  }
  fullPageFallbackSpent = true;
  recordFullPageRenewal(now);

  try {
    recoveryBudget.consume();
  } catch {
    markTerminal('iframe-renewal-fallback-budget-exhausted');
    return;
  }

  // The user is on a LIVE PAGE here (unlike attemptRecovery), so where they are
  // is worth preserving — in `state`, which is the only carrier the provider
  // accepts.
  const deepLink = signinStateFor();
  bootstrapState = { phase: 'idle', promise: null };
  sessionGeneration += 1;
  try {
    const organizationParams = resolveOrganizationParams();
    await getManager().signinRedirect({
      redirect_uri: callbackRedirectUri(),
      state: deepLink,
      ...(organizationParams ? { extraQueryParams: organizationParams } : {}),
    });
  } catch (err) {
    // The navigation itself failed; there is nothing left to try that would
    // not be a loop.
    Sentry.captureException(err, { tags: { component: 'scalekit-renewal-fallback' } });
    markTerminal('iframe-renewal-fallback-navigation-failed');
  }
};

// ───────────────────────────────────────────────────────────────────────────
// Session bootstrap
// ───────────────────────────────────────────────────────────────────────────

const runBootstrap = async (): Promise<boolean> => {
  const um = getManager();

  if (hasAuthorizationResponse()) {
    const user = await um.signinCallback(window.location.href);
    if (!user) {
      throw new Error('scalekit-callback-produced-no-session');
    }
    await assertTrustedIdentity(user, um);
    // Put the user back where they were AND strip `?code=…&state=…`, BEFORE
    // anything else can observe the URL. The stripping half is the precondition
    // AuthContext relies on to start Sentry Session Replay without leaking the
    // callback params into the replay envelope.
    restorePostLoginLocation(user.state);
    return true;
  }

  // No callback in the URL: probe the SSO cookie with a `prompt=none` iframe.
  // This is the exact equivalent of keycloak's `onLoad: 'check-sso'` — it
  // establishes a session for an already-signed-in user without ever forcing
  // an anonymous visitor through a login screen.
  try {
    const organizationParams = resolveOrganizationParams();
    const user = await um.signinSilent({
      prompt: 'none',
      ...(organizationParams ? { extraQueryParams: organizationParams } : {}),
    });
    if (!user) return false;
    await assertTrustedIdentity(user, um);
    return true;
  } catch (err) {
    // `oauthErrorCode` covers anything the provider ANSWERED with: the probe is
    // best effort, so every answer to it reads "no session could be established
    // without interaction". A misconfigured client must render a login screen,
    // not bounce the browser at an authorize URL that fails the same way.
    if (isSessionAbsent(err) || isIframeRenewalUnavailable(err) || oauthErrorCode(err)) {
      // Anonymous, deliberately. An iframe that cannot run is not evidence
      // the user has no session, but it IS evidence that redirecting them is
      // the wrong move on a cold load — the SPA renders its login affordance
      // and the user decides.
      return false;
    }
    throw err;
  }
};

export const startSession = (): Promise<boolean> => {
  ensureSecureContext();
  if (bootstrapState.phase === 'success' || bootstrapState.phase === 'pending') {
    return bootstrapState.promise!;
  }

  const promise: Promise<boolean> = runBootstrap()
    .then((authenticated) => {
      bootstrapState = { phase: 'success', promise };
      // A session invalidates any earlier recovery attempt budget: a user who
      // signs in successfully must not carry a half-spent budget into an
      // unrelated failure later in the same tab.
      //
      // `FULL_PAGE_RENEWAL_AT_KEY` is deliberately NOT cleared here. A
      // successful bootstrap is exactly what a full-page renewal produces, so
      // clearing it would hand the next renewal a fresh budget and a fresh
      // per-page flag — an unbounded redirect cycle assembled entirely out of
      // successful steps.
      recoveryBudget.clear();
      fullPageFallbackSpent = false;
      recoveryTerminal = false;
      return authenticated;
    })
    .catch(async (err) => {
      // A transient network blip during the code→token exchange (very common
      // on mobile WebKit, which rejects fetch() with "Load failed") isn't
      // actionable — attemptRecovery() below re-drives the flow. Only capture
      // a real, persistent failure. Same split as the keycloak adapter.
      if (isTransientAuthError(err)) {
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'scalekit-bootstrap transient network failure (recovery scheduled)',
        });
      } else {
        Sentry.captureException(err, { tags: { component: 'scalekit-bootstrap' } });
      }
      bootstrapState = { phase: 'failed', promise };
      return attemptRecovery();
    });

  bootstrapState = { phase: 'pending', promise };
  return promise;
};

// ───────────────────────────────────────────────────────────────────────────
// Token retrieval and renewal
// ───────────────────────────────────────────────────────────────────────────

const needsRenewal = (user: User): boolean => {
  const remaining = user.expires_in;
  if (typeof remaining !== 'number') return false;
  return remaining <= SCALEKIT_TOKEN_MIN_VALIDITY_SECONDS;
};

const renewAccessToken = (): Promise<string | null> => {
  if (inFlightRenewal) return inFlightRenewal;
  // Terminal means STOP. Without this, a page that keeps calling the API after
  // the ladder gave up opens a fresh hidden iframe against the provider for
  // every request — a redirect loop's quieter cousin, paced by the SDK's
  // 10-second frame timeout instead of by navigations. Cleared by the next
  // successful bootstrap, so a recovered session renews normally.
  if (recoveryTerminal) return Promise.resolve(null);

  const generation = sessionGeneration;
  const um = getManager();

  const renewal = (async (): Promise<string | null> => {
    try {
      const organizationParams = resolveOrganizationParams();
      const user = await um.signinSilent({
        prompt: 'none',
        ...(organizationParams ? { extraQueryParams: organizationParams } : {}),
      });
      if (generation !== sessionGeneration) return null;
      if (!user) return null;
      await assertTrustedIdentity(user, um);
      // Re-checked AFTER the assert as well: `assertTrustedIdentity` awaits, so
      // a logout can land inside that one microtask window. Without this, the
      // renewal would hand the caller a token for a session the user has
      // already ended — the last replay hole the generation counter exists to
      // close.
      if (generation !== sessionGeneration) return null;
      return user.access_token ?? null;
    } catch (err) {
      if (generation !== sessionGeneration) return null;
      if (isIframeRenewalUnavailable(err)) {
        await beginFullPageRenewalFallback();
        return null;
      }
      if (isSessionAbsent(err)) {
        // The iframe completed its round trip and the authorization server
        // definitively answered "this session is gone". Degrading to `null`
        // alone would strand the user on a broken logged-in dashboard: the
        // in-memory user stays loaded, AuthContext keeps `authenticated=true`,
        // and every API call goes out unauthenticated. keycloak-js handles the
        // same answer with `clearToken()` → `onAuthLogout`; mirror that by
        // unloading the user, which raises `userUnloaded` → `onLoggedOut` so
        // the SPA renders its login affordance. STILL no redirect — that is
        // what closes a login loop — and the bootstrap state machine is left
        // at 'success', so the next attempt is a renewal, not a second
        // bootstrap racing the first.
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'scalekit renewal answered session-gone (session unloaded)',
        });
        try {
          await um.removeUser();
        } catch {
          // ignore — null below is what fails closed.
        }
        currentSubject = undefined;
        return null;
      }
      // Everything else — network down, provider unreachable — is transient
      // evidence at best. The session stays loaded and renewable so a
      // recovered network can renew normally; the caller just gets no token.
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'warning',
        message: 'scalekit silent renewal failed (no token issued)',
        data: { transient: isTransientAuthError(err) },
      });
      return null;
    } finally {
      // Only the renewal that belongs to the CURRENT session generation may
      // clear the slot. A teardown (logout / recovery) already nulled it and
      // may have started a fresh renewal underneath us; clearing that one
      // would let two renewals run concurrently, which is the exact
      // single-flight guarantee this exists to provide.
      if (generation === sessionGeneration) inFlightRenewal = null;
    }
  })();

  inFlightRenewal = renewal;
  return renewal;
};

/**
 * A currently-valid access token, silently renewed when it is within
 * {@link SCALEKIT_TOKEN_MIN_VALIDITY_SECONDS} of expiry; `null` when there is
 * no usable session.
 *
 * Never throws and never caches the token string: it is read out of the
 * in-memory user store on every call, so a rotated token cannot be replayed
 * from this adapter after a renewal or a logout.
 */
export const getAccessToken = async (): Promise<string | null> => {
  ensureSecureContext();
  if (bootstrapState.phase !== 'success') return null;

  const um = getManager();
  // Recorded on every freshness decision, not only on renewals that fire —
  // "how close to expiry does this adapter tolerate" is a property of the
  // policy, not of whether it happened to trigger this time.
  lastRenewalMinValiditySeconds = SCALEKIT_TOKEN_MIN_VALIDITY_SECONDS;

  const generation = sessionGeneration;
  const user = await um.getUser();
  // The store read is asynchronous, so a logout (or a recovery) can land
  // between the phase check above and the answer below. Without this the
  // adapter would hand out a token for a session the user has already left.
  if (generation !== sessionGeneration) return null;
  if (!user || !user.access_token) return null;
  if (!needsRenewal(user)) return user.access_token;

  return renewAccessToken();
};

// ───────────────────────────────────────────────────────────────────────────
// Login / logout
// ───────────────────────────────────────────────────────────────────────────

export const beginLogin = async (redirectPath?: string): Promise<void> => {
  ensureSecureContext();
  // The redirect_uri is the fixed registered route; the deep link is captured
  // into `state`. signinStateFor() is OAuth-param-clean even when
  // window.location is contaminated, so callers never need to sanitise up front.
  const organizationParams = resolveOrganizationParams();
  await getManager().signinRedirect({
    redirect_uri: callbackRedirectUri(),
    state: signinStateFor(redirectPath),
    ...(organizationParams ? { extraQueryParams: organizationParams } : {}),
  });
};

export const beginFederatedLogin = async (redirectPath?: string): Promise<void> => {
  ensureSecureContext();
  const organizationParams = resolveOrganizationParams();
  const extraQueryParams = {
    ...(organizationParams ?? {}),
    ...resolveFederatedParams(organizationParams !== undefined),
  };
  await getManager().signinRedirect({
    redirect_uri: callbackRedirectUri(),
    state: signinStateFor(redirectPath),
    ...(Object.keys(extraQueryParams).length > 0 ? { extraQueryParams } : {}),
  });
};

export const endSession = async (): Promise<void> => {
  const um = getManager();
  // Tear the local session down FIRST, so a logout that never completes its
  // provider round trip still leaves no token behind in this tab. The
  // generation bump is deliberately synchronous: an in-flight renewal must
  // observe it before any of the awaits below yield.
  sessionGeneration += 1;
  inFlightRenewal = null;
  bootstrapState = { phase: 'idle', promise: null };
  currentSubject = undefined;
  // Capture the id_token BEFORE the user is removed: oidc-client-ts populates
  // `id_token_hint` from the STORED user, so removing first would send the
  // end-session request without it (RP-Initiated Logout §2 makes the hint
  // RECOMMENDED, and keycloak-js sends it). On a provider that requires it,
  // a hint-less logout is local-only — the upstream SSO session survives and
  // the next login signs the user straight back in. Passing it explicitly
  // keeps the remove-first memory hygiene AND the hint.
  let idTokenHint: string | undefined;
  try {
    const user = await um.getUser();
    idTokenHint = user?.id_token ?? undefined;
  } catch {
    // ignore — logout proceeds without the hint.
  }
  try {
    await um.removeUser();
  } catch {
    // ignore — the state above is already gone.
  }
  await um.signoutRedirect({
    post_logout_redirect_uri: buildNeutralRedirectUri('/'),
    // organization_id is an authorization-endpoint routing parameter, not an
    // RP-initiated-logout parameter. Override the settings-level default so it
    // cannot leak onto an endpoint that may reject unknown query fields.
    extraQueryParams: {},
    ...(idTokenHint ? { id_token_hint: idTokenHint } : {}),
  });
};

// ───────────────────────────────────────────────────────────────────────────
// Session events
// ───────────────────────────────────────────────────────────────────────────

export type ScalekitSessionHandlers = {
  onAuthenticated?: () => void;
  onLoggedOut?: () => void;
  onTokenExpired?: () => void;
};

/**
 * Bridge the SDK's event surface to the provider-neutral handlers AuthContext
 * consumes. Deliberately does NOT wire `silentRenewError` to the recovery
 * ladder: with `automaticSilentRenew: false` nothing raises it except a
 * renewal we already handle in {@link renewAccessToken}, and routing it to a
 * redirect would reintroduce exactly the dead-session login loop the contract
 * forbids.
 */
export const subscribeToSessionEvents = (
  handlers: ScalekitSessionHandlers,
): (() => void) => {
  const um = getManager();

  const onUserLoaded = (user: User): void => {
    currentSubject = user.profile?.sub;
    handlers.onAuthenticated?.();
  };
  const onUserUnloaded = (): void => {
    currentSubject = undefined;
    handlers.onLoggedOut?.();
  };
  const onAccessTokenExpiring = (): void => {
    handlers.onTokenExpired?.();
  };

  um.events.addUserLoaded(onUserLoaded);
  um.events.addUserUnloaded(onUserUnloaded);
  um.events.addAccessTokenExpiring(onAccessTokenExpiring);

  return () => {
    um.events.removeUserLoaded(onUserLoaded);
    um.events.removeUserUnloaded(onUserUnloaded);
    um.events.removeAccessTokenExpiring(onAccessTokenExpiring);
  };
};

/**
 * Complete the `prompt=none` handshake inside the renewal iframe. Called from
 * `src/main.tsx` BEFORE React boots — the iframe must post its result to the
 * opener and do nothing else. Constructing a bare UserManager (rather than
 * reusing {@link getManager}) keeps the frame free of the parent's session
 * state, which lives in a different JS realm anyway.
 */
export const completeSilentCallback = async (): Promise<void> => {
  try {
    await new UserManager(buildSettings()).signinSilentCallback(window.location.href);
  } catch (err) {
    // The parent frame times out and falls back; there is no UI here to tell.
    Sentry.addBreadcrumb({
      category: 'auth',
      level: 'warning',
      message: 'scalekit silent callback failed inside renewal iframe',
      data: { error: errorText(err) },
    });
  }
};
